import {
  useState,
  useEffect
} from "react";
import {
  useNavigate,
  Link
} from "react-router-dom";
import {
  sendForgotOtp
} from "../api/passwordApi";
import {
  Mail,
  ArrowRight,
  GraduationCap,
  KeyRound,
  ChevronLeft,
  ShieldCheck,
  LockKeyhole
} from "lucide-react";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Input
} from "../components/ui";
import {
  useToast
} from "../context/ToastContext";

const ForgotPassword = () => {
  const navigate = useNavigate();

  // Functional States
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // Parallax Ambient Light Effect Simulation
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      await sendForgotOtp(email);

      // Programmatic navigation forwarding user workflow payload state
      navigate("/verify-reset-otp", {
        state: { email },
      });
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to send reset OTP. Please check your credentials."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50/50 text-neutral-900 dark:text-neutral-100 flex flex-col antialiased">
      
      {/* Top Application Header Navigation Layer */}
      <header className="w-full h-14 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between px-4 sm:px-8">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:text-primary-400">
            <GraduationCap className="w-5 h-5" />
          </div>
          <span className="text-base font-bold tracking-tight text-neutral-900 dark:text-neutral-100">
            EduClarify ERP
          </span>
        </div>
        
        <div className="flex items-center gap-3">
          <button className="text-xs font-medium text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-100 transition-colors">
            Support
          </button>
          <Link 
            to="/login" 
            className="px-3 py-1.5 bg-neutral-900 text-white rounded-lg text-xs font-medium hover:bg-neutral-800 transition-all shadow-xs"
          >
            Log In
          </Link>
        </div>
      </header>

      {/* Main Structural Layout Content Container */}
      <main className="flex-grow flex items-center justify-center p-4 sm:p-6 relative z-10">
        
        {/* Subtle Ambient Background Accents */}
        <div 
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-50/60 rounded-full filter blur-3xl -z-10 pointer-events-none transition-transform duration-300 ease-out"
          style={{ transform: `translate(${mousePos.x * 20}px, ${mousePos.y * 20}px)` }}
        />
        <div 
          className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-neutral-100 dark:bg-neutral-800 rounded-full filter blur-3xl -z-10 pointer-events-none transition-transform duration-300 ease-out"
          style={{ transform: `translate(${mousePos.x * 40}px, ${mousePos.y * 40}px)` }}
        />

        <div className="w-full max-w-md space-y-6">
          
          {/* Main Transaction Card */}
          <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900">
            <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50 px-6 py-4 text-center">
              <div className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-primary-50 text-primary-600 dark:text-primary-400">
                <KeyRound className="w-5 h-5" />
              </div>
              <CardTitle className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                Forgot Password?
              </CardTitle>
              <p className="text-xs text-neutral-400 dark:text-neutral-500 font-normal leading-relaxed mt-1">
                Enter your registered work email and we'll send you a verification code to reset your password.
              </p>
            </CardHeader>

            <CardContent className="p-6 space-y-4">
              {/* Runtime Error Display Exception Framework */}
              

              {/* Password Reset Execution Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Input
                    label="Work Email Address"
                    id="email"
                    type="email"
                    placeholder="name@school.edu"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9"
                  />
                  <Mail className="w-4 h-4 text-neutral-400 dark:text-neutral-500 absolute left-3 top-[34px]" />
                </div>

                {/* Functional Trigger Action Submit Button CTA */}
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={loading}
                  rightIcon={<ArrowRight className="w-4 h-4" />}
                  className="w-full text-xs font-semibold py-2.5"
                >
                  {loading ? "Sending Reset Code..." : "Send Reset OTP"}
                </Button>
              </form>

              {/* Traversal Back-Link Anchor Node Context Row */}
              <div className="pt-2 text-center">
                <Link 
                  to="/login" 
                  className="inline-flex items-center gap-1 text-xs text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 font-medium transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Back to Login
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Infrastructure Security Layer Verification Trust Indicators */}
          <div className="flex items-center justify-center gap-4 text-neutral-400 dark:text-neutral-500 text-[10px] uppercase tracking-widest font-semibold">
            <div className="flex items-center gap-1">
              <LockKeyhole className="w-3.5 h-3.5" />
              <span>Secure Verification</span>
            </div>
            <span>•</span>
            <div className="flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>Enterprise Guard</span>
            </div>
          </div>

        </div>
      </main>

      {/* Structured Legal Portal Layout Footer */}
      <footer className="py-4 border-t border-neutral-200 dark:border-neutral-800 text-center bg-white dark:bg-neutral-900 text-xs text-neutral-400 dark:text-neutral-500">
        © {new Date().getFullYear()} Oakwood Academy Admin Portal. Powered by EduClarify ERP.
      </footer>

    </div>
  );
};

export default ForgotPassword;